import styled from 'styled-components'
import CourseEditableHeader from './CourseEditableHeader'
import { colors } from '../utils/config'
import CourseEditableYoutube from './CourseEditableYoutube'
import CourseEditableImage from './CourseEditableImage'
import CourseEditableDocument from './CourseEditableDocument'
import CourseEditableRichText from './CourseEditableRichText'
import { PropsWithChildren } from 'react'
import { CourseContent, LucideIconName } from '@bm-js/atlascloud-shared'
import { Icon } from '@bm-js/atlascloud-ui'

const StyledCourseEditableContentWrapper = styled.div`
  border-left: 1px solid ${colors.primary400};
  border-top: 1px solid ${colors.primary400};
  padding: 0.5rem 0.5rem 0rem 0.5rem;
  border-radius: 4px 0 0 0;
  margin-bottom: 1.5rem;
  .editable-content-headers {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: ${colors.black};
    font-weight: 600;
    border-bottom: 1px solid ${colors.gray400};
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
    .editable-content-actions {
      display: flex;
      align-items: center;
      gap: 0.2rem;
      .svg-button {
        cursor: pointer;
      }
    }
  }
`

type WrapperProps = {
  deleteContent: () => void
  moveContentUp: () => void
  moveContentDown: () => void
  content: CourseContent
}

const CourseEditableContentWrapper = ({
  children,
  deleteContent,
  moveContentUp,
  moveContentDown,
  content,
}: PropsWithChildren<WrapperProps>) => {
  return (
    <StyledCourseEditableContentWrapper>
      <div className='editable-content-headers'>
        <h4 className='m0'>{content.name}</h4>
        <div className='editable-content-actions'>
          <div className='svg-button text-gray-700' onClick={moveContentDown}>
            <Icon name={LucideIconName.CircleArrowDown} className='size-5' />
          </div>
          <div className='svg-button' onClick={moveContentUp}>
            <Icon name={LucideIconName.CircleArrowUp} className='size-5' />
          </div>
          <div className='svg-button' onClick={deleteContent}>
            <Icon name={LucideIconName.Trash2} className='size-5' />
          </div>
        </div>
      </div>
      {children}
    </StyledCourseEditableContentWrapper>
  )
}

type CourseEditableContentProps = {
  content: CourseContent
  setContentData: (data: CourseContent['data']) => void
  deleteContent: () => void
  moveContentUp: () => void
  moveContentDown: () => void
}

const CourseEditableContent = (props: CourseEditableContentProps) => {
  const { content, setContentData } = props
  const { type } = content
  switch (type) {
    case 'richtext':
      return (
        <CourseEditableContentWrapper {...props}>
          <CourseEditableRichText
            content={content}
            setContentData={setContentData}
          />
        </CourseEditableContentWrapper>
      )
    case 'image':
      return (
        <CourseEditableContentWrapper {...props}>
          <CourseEditableImage
            content={content}
            setContentData={setContentData}
          />
        </CourseEditableContentWrapper>
      )
    case 'document':
      return (
        <CourseEditableContentWrapper {...props}>
          <CourseEditableDocument
            content={content}
            setContentData={setContentData}
          />
        </CourseEditableContentWrapper>
      )
    case 'ytvideo':
      return (
        <CourseEditableContentWrapper {...props}>
          <CourseEditableYoutube
            content={content}
            setContentData={setContentData}
          />
        </CourseEditableContentWrapper>
      )
    case 'header':
      return (
        <CourseEditableContentWrapper {...props}>
          <CourseEditableHeader
            content={content}
            setContentData={setContentData}
          />
        </CourseEditableContentWrapper>
      )
    default:
      return null
  }
}

export default CourseEditableContent
